import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./Step6.module.scss";
import global from "../../../styles/index.module.scss";
import { StepComponent } from "../../../components/common/StepComponent/StepComponent";
import { GlobalData, LocationState } from "../../../types";
import { Layout } from "../../../components/common/Layout/Layout";
import { StepHeader } from "../../../components/common/StepHeader/StepHeader";
import { useProductData } from "../hooks/useProductData";
import { useEstimatedPrice } from "../hooks/useEstimatedPrice";
import { encodeAndFilterImages } from "../utils/encodeFileToBase64";
import { estimateEndpoint } from "../../../endpoint";
import { getEnvironmentEndpoint } from "../../../utils/getEnvironmentEndpoint";
import { redirectTo404 } from "../../../utils/redirectTo404";

export interface Step6Props {
  data: GlobalData[];
  firstCollectionHandle: string | null;
  secondCollectionHandle: string | null;
  productHandle: string | null;
  setIsComplete: (isComplete: boolean) => void;
  expireText?: string;
  retoolExpireText?: string;
}

export const Step6: React.FC<Step6Props> = ({
  data,
  firstCollectionHandle,
  secondCollectionHandle,
  productHandle,
  setIsComplete,
  expireText,
  retoolExpireText,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    images: initialImages = {},
    email: initialEmail = "",
    bland: initialBland = "",
    request: initialRequest = "",
    prefecture: initialPrefecture = "",
    area: initialArea = "",
    material: initialMaterial,
  } = (location.state as LocationState) || {};

  const { collectionData, secondCollectionData, productData, options, variants } = useProductData({
    data,
    firstCollectionHandle,
    secondCollectionHandle,
    productHandle,
  });

  const estimatedPrice = useEstimatedPrice({ options, variants });

  // 素材選択が必要かどうかを判定
  const shouldDisplayMaterialRadio =
    collectionData &&
    [
      "メンズ-シューズ",
      "レディース-シューズ",
      "メンズ-スニーカー",
      "レディース-スニーカー",
      "バッグ-リュック-財布",
      "ベルトや革小物など",
    ].includes(collectionData.first_collection.handle);

  // データ検証関数
  const validateStepData = () => {
    const errors: string[] = [];

    // Step4（画像）の検証
    if (productData?.product.media) {
      // メイン画像フィールドがある場合（通常の選択肢）
      if (!initialImages || Object.keys(initialImages).length === 0) {
        errors.push("画像がアップロードされていません");
      } else {
        const hasValidImages = Object.values(initialImages).some((img) => img instanceof File || img instanceof Blob);
        if (!hasValidImages) {
          errors.push("有効な画像ファイルがありません");
        }
      }
    } else {
      // メイン画像フィールドがない場合（「その他」選択時）- 追加画像が必須
      if (
        !initialImages ||
        !initialImages.additional ||
        !Array.isArray(initialImages.additional) ||
        initialImages.additional.length === 0
      ) {
        errors.push("画像がアップロードされていません");
      } else {
        const hasValidAdditionalImages = initialImages.additional.some(
          (img: any) => img instanceof File || img instanceof Blob,
        );
        if (!hasValidAdditionalImages) {
          errors.push("有効な画像ファイルがありません");
        }
      }
    }

    // Step5（フォーム）の検証
    if (!initialEmail || !initialEmail.trim()) {
      errors.push("メールアドレスが入力されていません");
    } else {
      // 簡単なメールアドレス形式チェック
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      if (!emailRegex.test(initialEmail)) {
        errors.push("有効なメールアドレスを入力してください");
      }
    }

    if (!initialBland || !initialBland.trim()) {
      errors.push("ブランド名・メーカー名が入力されていません");
    }

    if (!initialRequest || !initialRequest.trim()) {
      errors.push("相談内容が入力されていません");
    }

    if (!initialPrefecture || !initialPrefecture.trim()) {
      errors.push("都道府県が選択されていません");
    }

    if (!initialArea || !initialArea.trim()) {
      errors.push("エリアが入力されていません");
    }

    // 素材選択が必要な場合の検証
    if (shouldDisplayMaterialRadio && (!initialMaterial || !initialMaterial.trim())) {
      errors.push("素材の選択が必要です");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const { isValid: isDataValid, errors: dataErrors } = validateStepData();

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      // 画像ファイルをBase64にエンコードする関数を呼び出し
      const filteredImages = await encodeAndFilterImages(initialImages, productData);

      // UTMパラメータからcampaign_idを取得
      const urlParams = new URLSearchParams(window.location.search);
      const campaignId = urlParams.get("utm_campaign");
      const utmContent = urlParams.get("utm_content");
      const utmTerm = urlParams.get("utm_term");

      // JSONデータの準備
      const jsonData = {
        event: "estimate",
        payload: {
          first_collection_id: collectionData?.first_collection.id,
          second_collection_id: secondCollectionData?.second_collection.id,
          product_id: productData?.product.id,
          item_name: collectionData?.first_collection.title,
          repair_location: secondCollectionData?.second_collection.title,
          repair_menu_name: productData?.product.title,
          repair_menu_worry: productData?.product_metafields.metafields._nayami,
          estimated_time_in_store: productData?.product_metafields.metafields["_delivery-date-store"],
          estimated_time_delivery: productData?.product_metafields.metafields["_delivery-date-home-delivery"],
          email: initialEmail,
          request: initialRequest,
          estimated_price: estimatedPrice,
          images: filteredImages,
          brand_name: initialBland,
          material: initialMaterial,
          prefecture: initialPrefecture,
          usage_area: initialArea,
          campaign_id: campaignId,
          utm_content: utmContent,
          utm_term: utmTerm,
          delivery_attention_note: retoolExpireText?.trim() ? retoolExpireText.replace(/\n\s*/g, "").trim() : undefined,
        },
      };

      console.log(jsonData);

      // JSONデータを表示（デバッグ用）
      console.log("送信データ:", JSON.stringify(jsonData, null, 2));

      const endpoint = getEnvironmentEndpoint(estimateEndpoint);

      // axiosを使用してデータを送信
      const response = await axios.post(endpoint, jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("送信成功:", response.data);
      setIsComplete(true); // isCompleteを更新

      // 現在のURLパラメータを取得してcompleteページへの遷移時に保持
      const currentParams = new URLSearchParams(window.location.search);
      const completeUrl = currentParams.toString() ? `/complete?${currentParams.toString()}` : "/complete";
      navigate(completeUrl);
    } catch (error) {
      console.error("送信エラー:", error);
    } finally {
      setIsLoading(false); // ローディング状態をfalseに設定
    }
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  if (!firstCollectionHandle || !secondCollectionHandle || !productHandle) {
    redirectTo404();
    return null;
  }

  // 送信ボタンの無効化条件
  const isSubmitDisabled = !isChecked || isLoading || !isDataValid;

  return (
    <div className={styles.step6}>
      <Layout>
        <StepHeader stepNumber={6} title="ご記入の内容をご確認ください" firstcollectionhandle={firstCollectionHandle} />
      </Layout>
      <div className={styles.StepLayout}>
        {firstCollectionHandle !== "suits_campaign" && (
          <>
            <StepComponent
              to={`/?step=1`}
              category={collectionData?.first_collection.title}
              stepNumber={1}
              firstCollectionHandle={firstCollectionHandle}
            />
            <StepComponent
              to={`/?step=2&firstcollectionhandle=${firstCollectionHandle}`}
              category={secondCollectionData?.second_collection.title}
              stepNumber={2}
              firstCollectionHandle={firstCollectionHandle}
              secondCollectionHandle={secondCollectionHandle}
            />
          </>
        )}
        <StepComponent
          to={`/?step=3&firstcollectionhandle=${firstCollectionHandle}&secondcollectionhandle=${secondCollectionHandle}`}
          category={productData?.product.title}
          stepNumber={3}
          firstCollectionHandle={firstCollectionHandle}
          secondCollectionHandle={secondCollectionHandle}
          productHandle={productHandle}
          nayami={productData?.product_metafields.metafields._nayami}
          content={productData?.product.content}
          options={options}
          variants={variants}
          shop={productData?.product_metafields.metafields["_delivery-date-store"]}
          factory={productData?.product_metafields.metafields["_delivery-date-home-delivery"]}
        />
        <StepComponent
          to={`/?step=4&firstcollectionhandle=${firstCollectionHandle}&secondcollectionhandle=${secondCollectionHandle}&producthandle=${productHandle}`}
          category="画像アップロード"
          stepNumber={4}
          firstCollectionHandle={firstCollectionHandle}
        />
        <StepComponent
          to={`/?step=5&firstcollectionhandle=${firstCollectionHandle}&secondcollectionhandle=${secondCollectionHandle}&producthandle=${productHandle}`}
          category="修理相談内容"
          stepNumber={5}
          email={initialEmail}
          bland={initialBland}
          request={initialRequest}
          prefecture={initialPrefecture}
          area={initialArea}
          firstCollectionHandle={firstCollectionHandle}
          material={initialMaterial}
        />
      </div>
      <Layout>
        {/* データ不足時のエラー表示 */}
        {!isDataValid && (
          <div className={styles.dataErrorWrapper}>
            <p className={styles.dataErrorTitle}>入力データが不足しています</p>
            <ul className={styles.dataErrorList}>
              {dataErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
            <p className={styles.dataErrorGuide}>
              前のSTEPに戻って必要な情報を入力してください。
              <br />
              入力内容はリセットされます。
            </p>
          </div>
        )}
        <div className={styles.submitWrapper}>
          <p className={styles.submitTitle}>こちらの内容で送信しますか？</p>
          <div className={global.operatorWrapper}>
            <div className={`${global.operatorLabelWrapper}`}>
              <div className={global.operatorLabelInner}>
                <p className={global.operatorLabelTitle}>回答までの日数（目安）</p>
                <div className={global.operatorLabel}>
                  {expireText?.trim() && <div dangerouslySetInnerHTML={{ __html: expireText }} />}
                </div>
              </div>
            </div>
          </div>
          <p className={styles.submitText}>
            当社より回答するメール・内容はお客様個人にあててお送りするものです。当社からの回答内容の一部または全部を転用・二次利用することはご遠慮ください。
          </p>
          {/* <div className={styles.policyBody} dangerouslySetInnerHTML={{ __html: policyBody }} /> */}
          <label className={styles.submitCheckLabel} htmlFor="privacyPolicyCheckbox">
            <input type="checkbox" id="privacyPolicyCheckbox" checked={isChecked} onChange={handleCheckboxChange} />
            <a href="https://www.minit.co.jp/privacy/" target="_blank" rel="noopener">
              プライバシーポリシー
            </a>
            について同意する（必須）
          </label>
        </div>
      </Layout>
      <div className={styles.buttonWrapper}>
        <button
          className={`${global.primaryButton} ${"js-trackEvent"} ${isSubmitDisabled ? global.disabled : ""}`}
          onClick={() => void onSubmit()}
          disabled={isSubmitDisabled}
          data-ga-id="Clk_Btn_AppPage6_NextBtn"
        >
          {isLoading ? "送信中..." : "送信する"}
        </button>
        <p className={styles.alertMessage}>ブラウザの「戻る」ボタンは使わないでください</p>
      </div>
    </div>
  );
};
